import { css } from '@emotion/react';
import { space } from 'folio-common-components';
import * as React from 'react';
import type { MaBankSelection } from '../../../gqltypes';
import { setBankSelection } from '../../../services';
import { type RootState, useActions, useSelector } from '../../../state';
import { PageHeading } from '../../page-heading';

function selector(state: RootState) {
  return {
    id: state.founding.id,
    documentUrl: state.founding.documentURL,
    bankSelection: state.founding.bankSelection,
  };
}

export const OtherBankAllSigned: React.FC = () => {
  const { documentUrl, id, bankSelection } = useSelector(selector);
  const { foundingActions } = useActions();
  const onSetBankSelection = async (selection: MaBankSelection) => {
    await setBankSelection({ id, selection });
    foundingActions.updateFounding({
      bankSelection: selection,
    });

    // This will show another screen, but not change the URL
    // so we have to scroll manually.
    window.scrollTo(0, 0);
  };

  return (
    <>
      <PageHeading>Stifte AS med en annen banktjeneste</PageHeading>

      <p>
        Bruk stiftelses&shy;dokumentet til å opprette konto i en bank etter eget
        ønske. Etterpå registrerer du selskapet i Altinn selv, eller med hjelp
        fra banken din.
      </p>

      <p>
        <strong>Slik går du fram</strong>
      </p>

      <ol
        css={css`
          padding-left: 24px;

          li:not(:first-of-type) {
            ${space([8], 'margin-top')}
          }
        `}
      >
        <li>
          <a href={documentUrl || undefined} download="Stiftelsesdokument.pdf">
            Last ned stiftelses&shy;dokumentet
          </a>
          , sånn at du har det når banken spør. Vi sender det til deg på e-post
          også, for sikkerhets skyld.
        </li>
        <li>
          <b>Finn en bank</b> som tilbyr aksje&shy;kapital&shy;konto til
          selskaper under stiftelse.
        </li>
        <li>
          <b>Sett inn aksjekapitalen</b> på konto.
        </li>
        <li>
          <b>Få en bekreftelse fra banken</b> om at pengene er på konto. Det er
          vanligvis en PDF-fil. Det hender de tar et lite gebyr.
        </li>

        <li>
          <b>Send inn samordnet register&shy;melding</b>. Du fyller inn{' '}
          <a href="https://info.altinn.no/skjemaoversikt/bronnoysundregistrene/registrere-nye-og-endre-eksisterende-foretak-og-enheter---samordnet-registermelding/">
            skjemaet i Altinn
          </a>{' '}
          for å registrere AS. Her legger du ved stiftelses&shy;dokumentet og
          bekreftelsen fra banken din.
        </li>

        <li>
          <b>Vent på at selskapet registreres.</b> Du må regne med å vente 2–4
          uker.
        </li>
      </ol>

      {bankSelection === 'OTHER_NAGGED' ? (
        <div
          css={css`
            background: var(--green-300);
            ${space([16], 'padding')};
            border-radius: var(--border-radius);
          `}
        >
          Skal vi heller gjøre alt dette for deg?{' '}
          <a
            href={window.location.href}
            onClick={async event => {
              event.preventDefault();
              await onSetBankSelection('UNSET');
            }}
          >
            Åpne Folio-konto for selskapet nå
          </a>
          .
        </div>
      ) : null}
    </>
  );
};
